import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { btn } from '../../utils/buttonStyles';

/**
 * The two dialogs that ask which events in a recurring series an action should
 * touch.
 *
 * Both previously lived as byte-identical copies inside CalendarAndEvent and
 * EventAttendance, styled with `bg-white dark:bg-gray-800`. That `dark:` never
 * fires here — the app is on Tailwind v4, where class-based dark mode needs a
 * `@custom-variant` the stylesheet does not declare, so the variant follows the
 * operating system rather than the theme the user picked. On a dark theme the
 * dialog came out white with near-white text. Theme tokens are the only thing
 * that tracks the real theme, so they are what these use.
 *
 * Neither dialog had a way out except choosing an option; both now close on the
 * X, on the backdrop, and on Escape.
 */

/** Shared shell: backdrop, panel, heading, close affordance. */
function ModalShell({ title, description, onClose, children }) {
  const { theme, isDark } = useTheme();

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
    >
      <div
        className={`${theme.card} rounded-2xl p-6 max-w-sm w-full shadow-xl relative`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={`${btn.iconSm} absolute top-3 right-3 ${theme.textMuted} ${
            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
          }`}
        >
          <X className="w-4 h-4" />
        </button>

        <h3 className={`text-base font-semibold ${theme.text} mb-1 pr-8`}>{title}</h3>
        <p className={`text-sm ${theme.textSecondary} mb-5`}>{description}</p>
        {children}
      </div>
    </div>
  );
}

/**
 * Asks whether an availability response applies to one event or the whole
 * series. Dismissing is not a third answer — it leaves the response alone.
 */
export function ApplyToSeriesConfirm({ isOpen, onConfirm, onCancel }) {
  const { theme, isDark } = useTheme();
  if (!isOpen) return null;

  return (
    <ModalShell
      title="Apply to series?"
      description="Do you want to apply this availability to all events in the series?"
      onClose={onCancel}
    >
      <div className="flex gap-3">
        <button
          onClick={() => onConfirm(false)}
          className={`${btn.base} ${btn.md} flex-1 border ${theme.border} ${theme.text} ${
            isDark ? 'hover:bg-white/10' : 'hover:bg-gray-50'
          }`}
        >
          Just this event
        </button>
        <button
          onClick={() => onConfirm(true)}
          className={`${btn.base} ${btn.md} flex-1 ${theme.buttonPrimary}`}
        >
          All events
        </button>
      </div>
    </ModalShell>
  );
}

/** Asks how far an edit or delete reaches through the series. */
export function RecurringScopeModal({ isOpen, onClose, onSelect, action }) {
  const { theme, isDark } = useTheme();
  if (!isOpen) return null;

  return (
    <ModalShell
      title={action === 'delete' ? 'Delete recurring event' : 'Edit recurring event'}
      description={`This event is part of a series. Which events do you want to ${action}?`}
      onClose={onClose}
    >
      <div className="space-y-2">
        {[
          { scope: 'this', label: 'Just this event' },
          { scope: 'following', label: 'This and all following events' },
          { scope: 'all', label: 'All events in the series' }
        ].map(({ scope, label }) => (
          <button
            key={scope}
            onClick={() => onSelect(scope)}
            className={`w-full text-left px-4 py-3 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium transition-colors ${
              isDark ? 'hover:bg-white/10' : 'hover:bg-blue-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <button
        onClick={onClose}
        className={`mt-3 w-full text-sm ${theme.textMuted} hover:${theme.text} py-2`}
      >
        Cancel
      </button>
    </ModalShell>
  );
}
